import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  Clock3,
  HeartPulse,
  Home,
  Search,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  XCircle,
} from "lucide-react";
import { FaWhatsapp as WhatsApp } from "react-icons/fa";
import { Header as GuestHeader, Footer as GuestFooter } from "../Home";
import ArticleSeo from "./ArticleSeo";
import { formatArticleDate } from "./articleData";
import "./articles.css";

export function SiteArticleHeader() {
  return (
    <>
      <GuestHeader />

    </>
  );
}

export function SiteArticleFooter() {
  return <GuestFooter />;
}

export function WhatsAppButton({
  href,
  children,
  compact = false,
  className = "",
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`article-focus inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] font-black text-[#05352e] shadow-sm transition-colors hover:bg-[#20bd5a] active:bg-[#1eac53] ${
        compact ? "px-4 py-2.5 text-sm" : "px-6 py-3.5 text-base"
      } ${className}`}
    >
      <WhatsApp aria-hidden="true" className="h-5 w-5" />
      {children}
    </a>
  );
}

export function ArticleLayout({ seo, children, stickyHref, structuredData }) {
  return (
    <div className="articles-surface">
      <ArticleSeo {...seo} structuredData={structuredData} />
      <SiteArticleHeader />
      {children}
      <SiteArticleFooter />
      <StickyWhatsAppCTA href={stickyHref} />
    </div>
  );
}

export function ArticleHeader({ article, consultationHref }) {
  const intro = Array.isArray(article.intro)
    ? article.intro.join(" ")
    : article.intro || article.excerpt;

  return (
    <header className="mx-auto max-w-5xl px-5 pb-10 pt-12 lg:px-8">
      <nav
        aria-label="Breadcrumb"
        className="mb-8 flex flex-wrap items-center gap-2 text-sm font-bold text-slate-500"
      >
        <Link
          to="/"
          className="article-focus rounded-full hover:text-[#075E54]"
        >
          Home
        </Link>
        <span>/</span>
        <Link
          to="/articles"
          className="article-focus rounded-full hover:text-[#075E54]"
        >
          Articles
        </Link>
        <span>/</span>
        <span>{article.category}</span>
        <span>/</span>
        <span className="text-slate-800">
          {article.shortTitle || article.title}
        </span>
      </nav>

      <div className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-[#075E54]">
        <HeartPulse size={14} />
        {article.category}
      </div>

      <h1 className="mt-6  max-w-4xl text-4xl font-black leading-[1.08] text-slate-950 sm:text-5xl lg:text-6xl">
        {article.title}
      </h1>

      <p className="mt-6 max-w-3xl text-lg font-semibold leading-8 text-slate-600">
        {intro}
      </p>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        {/* <MedicalReviewBadge /> */}
        <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 shadow-sm">
          <Clock3 size={16} className="text-[#075E54]" />
          {article.readingTime}
        </span>
        <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 shadow-sm">
          <CalendarDays size={16} className="text-[#075E54]" />
          Updated {formatArticleDate(article.dateModified)}
        </span>
      </div>

      <ArticleCTA
        eyebrow={article.topCta?.eyebrow || "Need medical help?"}
        title={
          article.topCta?.title || "Speak privately with a Zaviramed doctor."
        }
        body={
          article.topCta?.body ||
          "A doctor can help you understand your symptoms and advise on the safest next step."
        }
        href={consultationHref}
        buttonLabel={article.topCta?.buttonLabel || "Chat with a Doctor"}
        compact
      />
    </header>
  );
}

export function MedicalReviewBadge() {
  return (
    <span className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-black text-[#075E54]">
      <ShieldCheck size={16} />
      Medically reviewed
    </span>
  );
}

export function ArticleTableOfContents({ items }) {
  return (
    <nav
      aria-label="In this article"
      className="mx-auto mb-12 max-w-5xl rounded-2xl border border-slate-200 bg-white p-5 shadow-md sm:p-6"
    >
      <div className="mb-4 flex items-center gap-2">
        <BookOpen size={18} className="text-[#075E54]" />
        <h2 className="text-lg font-black text-slate-950">In this article</h2>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className="article-focus rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-extrabold text-slate-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-[#075E54]"
          >
            {item.label}
          </a>
        ))}
      </div>
    </nav>
  );
}

export function ArticleSection({ id, title, children }) {
  return (
    <section id={id} className="article-section mb-12">
      <h2 className="mb-5 text-2xl font-black leading-tight text-slate-950 sm:text-3xl">
        {title}
      </h2>
      <div className="article-prose">{children}</div>
    </section>
  );
}

export function ArticleCTA({
  eyebrow,
  title,
  body,
  href,
  buttonLabel,
  compact = false,
}) {
  return (
    <aside
      className={`my-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-md sm:p-7 ${
        compact ? "" : "lg:p-8"
      }`}
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {/* {eyebrow && (
            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-[#075E54]">
              {eyebrow}
            </p>
          )} */}
          <h2 className="text-2xl font-semibold leading-tight text-slate-950">
            {title}
          </h2>
          {/* <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-slate-600 sm:text-base">
            {body}
          </p> */}
        </div>
        <WhatsAppButton href={href} className="shrink-0">
          {buttonLabel}
        </WhatsAppButton>
      </div>
    </aside>
  );
}

export function DischargeColourGuide({ items }) {
  return (
    <div className="my-7 grid gap-3 sm:grid-cols-2">
      {items.map((item) => (
        <div
          key={item.title}
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        >
          <div className="mb-3 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#075E54]">
            {item.title}
          </div>
          <p className="text-sm font-semibold leading-7 text-slate-600">
            {item.body}
          </p>
        </div>
      ))}
    </div>
  );
}

export function WhatNotToDo({ items }) {
  return (
    <div className="my-7 grid gap-3 sm:grid-cols-2">
      {items.map((item) => (
        <div
          key={item}
          className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <XCircle size={19} className="mt-0.5 shrink-0 text-[#075E54]" />
          <p className="text-sm font-bold leading-6 text-slate-700">{item}</p>
        </div>
      ))}
    </div>
  );
}

export function RedFlagBox({ items }) {
  return (
    <aside className="my-7 rounded-2xl border-l-4 border-[#075E54] bg-emerald-50 p-6 shadow-sm">
      <div className="mb-5 flex items-center gap-3">
        {/* <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#075E54] text-white shadow-sm">
          <AlertTriangle size={21} />
        </div> */}
        <div>
          <h3 className="text-xl font-black text-slate-950">
            Seek medical assessment if you notice:
          </h3>
          <p className="mt-1 text-sm font-semibold text-slate-600">
            These signs do not always mean something dangerous, but they deserve
            proper assessment.
          </p>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((item) => (
          <div key={item} className="flex items-start gap-2">
            <CheckCircle2 size={17} className="mt-1 shrink-0 text-[#25D366]" />
            <span className="text-sm font-bold leading-6 text-slate-700">
              {item}
            </span>
          </div>
        ))}
      </div>
    </aside>
  );
}

export function ArticleFAQ({ items }) {
  return (
    <section id="faqs" className="article-section mt-16">
      <h2 className="mb-6 text-2xl font-black text-slate-950 sm:text-3xl">
        Frequently asked questions
      </h2>
      <div className="grid gap-3">
        {items.map((item) => (
          <details
            key={item.question}
            className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow open:shadow-md"
          >
            <summary className="article-focus cursor-pointer list-none rounded-xl text-base font-black text-slate-950">
              {item.question}
            </summary>
            <p className="mt-3 text-sm font-semibold leading-7 text-slate-600">
              {item.answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function RelatedArticles({ items }) {
  if (!items?.length) return null;

  return (
    <section className="mt-16">
      <div className="mb-5 flex items-center gap-2">
        <Sparkles size={18} className="text-[#075E54]" />
        <h2 className="text-2xl font-black text-slate-950">Related reading</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <Link
            key={item.title}
            to={item.slug ? `/articles/${item.slug}` : "/articles"}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
          >
            <h3 className="text-base font-black text-slate-950">
              {item.title}
            </h3>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              {item.status}
            </p>
          </Link>
        ))}
      </div>
      <p className="mt-5 text-sm font-semibold leading-7 text-slate-500">
        You can also read more about{" "}
        <a className="text-[#075E54] underline" href="/#about">
          Zaviramed
        </a>{" "}
        or start from the{" "}
        <a className="text-[#075E54] underline" href="/#top">
          consultation page
        </a>
        .
      </p>
    </section>
  );
}

export function ReferenceList({ items }) {
  return (
    <section className="mt-16">
      <h2 className="mb-5 text-2xl font-black text-slate-950">References</h2>
      <ol className="space-y-3 text-sm font-semibold leading-7 text-slate-600">
        {items.map((item) => (
          <li key={item.url}>
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="article-focus rounded-lg font-extrabold text-[#075E54] underline decoration-emerald-200 underline-offset-4"
            >
              {item.title}
            </a>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function EditorialDisclaimer() {
  return (
    <aside className="mt-12 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm font-semibold leading-7 text-slate-600 shadow-sm">
      <strong className="text-slate-950">Editorial note:</strong> Medical
      information on Zaviramed is for education and does not replace an
      individual medical assessment. If you feel severely unwell, have heavy
      bleeding, fainting, severe pain or emergency symptoms, seek urgent
      in-person care.
    </aside>
  );
}

export function StickyWhatsAppCTA({ href }) {
  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label="Talk to a doctor on WhatsApp"
      title="Talk to a doctor"
      className="
    sticky-whatsapp-cta article-focus fixed bottom-5 right-2 z-50
    flex h-14 w-14 items-center justify-center rounded-full
    bg-[#25D366] text-[#05352e] shadow-lg
    transition-all duration-200
    hover:-translate-y-1 hover:bg-[#20bd5a] hover:shadow-xl
    active:translate-y-0 active:bg-[#1eac53]
  "
    >
      <WhatsApp className="h-7 w-7" aria-hidden="true" />
    </a>
  );
}

export function ArticleDirectoryIllustration() {
  return (
    <div className="relative h-56 overflow-hidden rounded-2xl border border-slate-200 bg-emerald-50 shadow-md">
      <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-[64px] bg-[#25D366]/20" />
      <div className="absolute bottom-0 left-0 h-20 w-40 rounded-tr-[64px] bg-[#075E54]/10" />
      <div className="relative flex h-full flex-col justify-between p-6">
        <div className="flex items-center justify-between">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#075E54] text-white shadow-md">
            <Stethoscope size={25} />
          </div>
          <div className="rounded-lg bg-white px-3 py-1 text-xs font-black text-[#075E54] shadow-sm">
            Women&apos;s Health
          </div>
        </div>
        <div>
          <div className="mb-3 h-3 w-24 rounded-full bg-[#25D366]/45" />
          <div className="mb-2 h-3 w-44 rounded-full bg-slate-900/10" />
          <div className="h-3 w-32 rounded-full bg-slate-900/10" />
        </div>
      </div>
    </div>
  );
}

export function DirectorySearch({ value, onChange }) {
  return (
    <label className="flex items-center gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 shadow-sm transition-colors focus-within:border-[#075E54] focus-within:ring-2 focus-within:ring-emerald-100">
      <Search size={18} className="shrink-0 text-[#075E54]" />
      <span className="sr-only">Search health articles</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search articles"
        className="min-w-0 flex-1 rounded-full bg-transparent text-sm font-semibold text-slate-800 placeholder:text-slate-400 focus:outline-none"
      />
    </label>
  );
}

export function BackToArticles() {
  return (
    <Link
      to="/articles"
      className="article-focus mb-8 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-[#075E54] shadow-sm transition-colors hover:bg-emerald-50"
    >
      <ArrowLeft size={16} />
      Back to articles
    </Link>
  );
}

export function EmptyArticlesState() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-md">
      <Home className="mx-auto h-8 w-8 text-[#075E54]" />
      <h2 className="mt-4 text-xl font-black text-slate-950">
        No article found
      </h2>
      <p className="mt-2 text-sm font-semibold text-slate-500">
        Try another search term or clear the category filter.
      </p>
    </div>
  );
}

export function ReadArticleLink({ to }) {
  return (
    <Link
      to={to}
      className="article-focus inline-flex items-center justify-center gap-2 rounded-xl bg-[#075E54] px-5 py-3 text-sm font-black text-white shadow-sm transition-colors hover:bg-[#064a42] active:bg-[#053f39]"
    >
      Read article
      <ArrowRight size={16} />
    </Link>
  );
}

